import { Suspense, lazy } from 'react'
import { Toaster } from "@/components/ui/toaster"
import { QueryClientProvider } from '@tanstack/react-query'
import { queryClientInstance } from '@/lib/query-client'
import NavigationTracker from '@/lib/NavigationTracker'
import { pagesConfig } from './pages.config'
const EmployerResources = lazy(() => import('./pages/EmployerResources'));
const IndexingDashboard = lazy(() => import('./pages/IndexingDashboard'));
const SeoFreshness = lazy(() => import('./pages/SeoFreshness'));
const JobFeedReconciliation = lazy(() => import('./pages/JobFeedReconciliation'));
const StateSalaryGuide = lazy(() => import('./pages/StateSalaryGuide'));
const PrivacyPolicy = lazy(() => import('./pages/PrivacyPolicy'));
const Terms = lazy(() => import('./pages/Terms'));
const JobSpecialties = lazy(() => import('./pages/JobSpecialties'));
const SalaryCalculator = lazy(() => import('./pages/SalaryCalculator'));
const ROICalculator = lazy(() => import('./pages/ROICalculator'));
const SuccessStories = lazy(() => import('./pages/SuccessStories'));
const SpecialtyJobsByLocation = lazy(() => import('./pages/SpecialtyJobsByLocation'));
// SpecialtyJobs is rendered by JobsRoute (the /jobs/* dispatcher).
const JobsRoute = lazy(() => import('./pages/JobsRoute'));
const HealthcareJobsHub = lazy(() => import('./pages/HealthcareJobsHub'));
const JobsHub = lazy(() => import('./pages/JobsHub'));
const HealthcareJobsRoot = lazy(() => import('./pages/HealthcareJobsRoot'));
import { BrowserRouter as Router, Route, Routes, Navigate } from 'react-router-dom';
import PageNotFound from './lib/PageNotFound';
import ProtectedRoute from '@/components/ProtectedRoute';
import { AuthProvider, useAuth } from '@/lib/AuthContext';
import UserNotRegisteredError from '@/components/UserNotRegisteredError';
import RouteRobots from '@/components/RouteRobots';

const { Pages, Layout, mainPage } = pagesConfig;
const mainPageKey = mainPage ?? Object.keys(Pages)[0];
const MainPage = mainPageKey ? Pages[mainPageKey] : <></>;

const PageFallback = () => (
  <div className="flex items-center justify-center min-h-[50vh]">
    <div className="w-8 h-8 border-4 border-slate-200 border-t-teal-600 rounded-full animate-spin" />
  </div>
);

const LayoutWrapper = ({ children, currentPageName }) => Layout ?
  <Layout currentPageName={currentPageName}>
    <Suspense fallback={<PageFallback />}>
      {children}
    </Suspense>
  </Layout>
  : <Suspense fallback={<PageFallback />}>{children}</Suspense>;

// Routes requiring authentication. null = any authenticated user; array = allowed user_types.
const PROTECTED_ROUTES = {
  'AccountSettings': null,
  'ApplicationDetails': null,
  'Assessments': ['facility', 'staffing_agency'],
  'BlogAdmin': null,
  'CandidateInbox': ['worker'],
  'CandidateOfferLetter': ['worker'],
  'CandidateOnboarding': ['worker'],
  'CandidateRelationships': ['facility', 'staffing_agency'],
  'FacilityDashboard': ['facility'],
  'FacilityDocuments': ['facility', 'staffing_agency'],
  'FacilitySetup': ['facility'],
  'ManageAlerts': ['worker'],
  'Messages': null,
  'Network': ['worker'],
  'Onboarding': null,
  'Referrals': ['worker'],
  'SkillsProficiency': ['worker'],
  'StaffingAgencyDashboard': ['staffing_agency'],
  'StaffingAgencySetup': ['staffing_agency'],
  'TalentPool': ['facility', 'staffing_agency'],
  'TeamManagement': ['facility', 'staffing_agency'],
  'WorkerDashboard': ['worker'],
  'WorkerProfile': ['worker'],
};

const AuthenticatedApp = () => {
  const { isLoadingAuth, isLoadingPublicSettings, authError, navigateToLogin } = useAuth();

  // Show loading spinner while checking app public settings or auth
  if (isLoadingPublicSettings || isLoadingAuth) {
    return (
      <div className="fixed inset-0 flex items-center justify-center">
        <div className="w-8 h-8 border-4 border-slate-200 border-t-slate-800 rounded-full animate-spin"></div>
      </div>
    );
  }

  // Handle authentication errors
  if (authError) {
    if (authError.type === 'user_not_registered') {
      return <UserNotRegisteredError />;
    } else if (authError.type === 'auth_required') {
      // Redirect to login automatically
      navigateToLogin();
      return null;
    }
  }

  // Render the main app
  return (
    <Routes>
      <Route path="/" element={
        <LayoutWrapper currentPageName={mainPageKey}>
          <MainPage />
        </LayoutWrapper>
      } />
      <Route path="/Home" element={<Navigate to="/" replace />} />
      {Object.entries(Pages).map(([path, Page]) => {
        const protection = PROTECTED_ROUTES[path];
        return (
          <Route
            key={path}
            path={`/${path}`}
            element={
              <LayoutWrapper currentPageName={path}>
                {protection !== undefined ? (
                  <ProtectedRoute allowedTypes={protection}>
                    <Page />
                  </ProtectedRoute>
                ) : (
                  <Page />
                )}
              </LayoutWrapper>
            }
          />
        );
      })}
      <Route path="/JobSpecialties" element={
        <LayoutWrapper currentPageName="JobSpecialties">
          <JobSpecialties />
        </LayoutWrapper>
      } />
      <Route path="/SalaryCalculator" element={
        <LayoutWrapper currentPageName="SalaryCalculator">
          <SalaryCalculator />
        </LayoutWrapper>
      } />
      <Route path="/ROICalculator" element={
        <LayoutWrapper currentPageName="ROICalculator">
          <ROICalculator />
        </LayoutWrapper>
      } />
      <Route path="/SuccessStories" element={
        <LayoutWrapper currentPageName="SuccessStories">
          <SuccessStories />
        </LayoutWrapper>
      } />
      <Route path="/SpecialtyJobsByLocation" element={
        <LayoutWrapper currentPageName="SpecialtyJobsByLocation">
          <SpecialtyJobsByLocation />
        </LayoutWrapper>
      } />
      {/* Discovery roots — the breadcrumb parents for the two namespaces. */}
      <Route path="/jobs" element={
        <LayoutWrapper currentPageName="JobsHub">
          <JobsHub />
        </LayoutWrapper>
      } />
      <Route path="/healthcare-jobs" element={
        <LayoutWrapper currentPageName="HealthcareJobsRoot">
          <HealthcareJobsRoot />
        </LayoutWrapper>
      } />
      {/* /jobs/* — one dispatcher. Canonical profession URLs resolve to the
          profession landing page; every legacy shape falls through to the
          unchanged SpecialtyJobs renderer. Route params keep their original
          names so the legacy renderer reads exactly what it always has. */}
      <Route path="/jobs/:specialty" element={
        <LayoutWrapper currentPageName="JobsRoute">
          <JobsRoute />
        </LayoutWrapper>
      } />
      <Route path="/jobs/:specialty/:location" element={
        <LayoutWrapper currentPageName="JobsRoute">
          <JobsRoute />
        </LayoutWrapper>
      } />
      <Route path="/jobs/:specialty/:subspecialty/:location" element={
        <LayoutWrapper currentPageName="JobsRoute">
          <JobsRoute />
        </LayoutWrapper>
      } />
      {/* Distinct location namespace — never shares a shape with a profession. */}
      <Route path="/healthcare-jobs/:state" element={
        <LayoutWrapper currentPageName="HealthcareJobsHub">
          <HealthcareJobsHub />
        </LayoutWrapper>
      } />
      <Route path="/healthcare-jobs/:state/:city" element={
        <LayoutWrapper currentPageName="HealthcareJobsHub">
          <HealthcareJobsHub />
        </LayoutWrapper>
      } />
      <Route path="/PrivacyPolicy" element={
        <LayoutWrapper currentPageName="PrivacyPolicy">
          <PrivacyPolicy />
        </LayoutWrapper>
      } />
      <Route path="/Terms" element={
        <LayoutWrapper currentPageName="Terms">
          <Terms />
        </LayoutWrapper>
      } />
      <Route path="/salary-guide/:specialty/:state" element={
        <LayoutWrapper currentPageName="StateSalaryGuide">
          <StateSalaryGuide />
        </LayoutWrapper>
      } />
      <Route path="/EmployerResources" element={
        <LayoutWrapper currentPageName="EmployerResources">
          <EmployerResources />
        </LayoutWrapper>
      } />
      <Route path="/IndexingDashboard" element={
        <LayoutWrapper currentPageName="IndexingDashboard">
          <ProtectedRoute allowedTypes={null}>
            <IndexingDashboard />
          </ProtectedRoute>
        </LayoutWrapper>
      } />
      {/* Admin-only, read-only SEO freshness simulation. Same treatment as the
          reconaissance dashboards: authentication first, admin role in-page. */}
      <Route path="/SeoFreshness" element={
        <LayoutWrapper currentPageName="SeoFreshness">
          <ProtectedRoute allowedTypes={null}>
            <SeoFreshness />
          </ProtectedRoute>
        </LayoutWrapper>
      } />
      {/* Admin-only, read-only. ProtectedRoute resolves authentication; the
          page itself enforces the administrator role, exactly as the Google
          Indexing Monitor does. Excluded from indexing in index.html. */}
      <Route path="/JobFeedReconciliation" element={
        <LayoutWrapper currentPageName="JobFeedReconciliation">
          <ProtectedRoute allowedTypes={null}>
            <JobFeedReconciliation />
          </ProtectedRoute>
        </LayoutWrapper>
      } />
      <Route path="*" element={<PageNotFound />} />
    </Routes>
  );
};


function App() {

  return (
    <AuthProvider>
      <QueryClientProvider client={queryClientInstance}>
        <Router>
          <NavigationTracker />
          <AuthenticatedApp />
          {/* Owns the robots directive for the private routes that cannot emit
              one themselves, and removes it again on the way out. */}
          <RouteRobots />
        </Router>
        <Toaster />
      </QueryClientProvider>
    </AuthProvider>
  )
}

export default App